/**
 * PromptModelGrid
 *
 * Primary eval view: prompts on Y-axis, models on X-axis.
 * Each cell shows the weightedScore from the latest report for that
 * (prompt, model) pair. Clicking a cell navigates to the report detail.
 * Empty cells can trigger an eval run.
 */

import React, {useMemo} from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {formatCost, formatPricePer1M} from '../../utils/format';

interface ReportSummary {
  id: string;
  promptName: string;
  promptExists?: boolean;
  modelName?: string | null;
  ranAt: string;
  weightedScore?: number | null;
  passed: number;
  totalCases: number;
  totalCost?: number | null;
}

interface PromptSummary {
  name: string;
  displayName?: string | null;
}

/** A running job for a (prompt, model) cell */
export interface GridCellJob {
  promptName: string;
  modelName: string;
  jobId: string;
}

interface ModelPricing {
  inputPer1M: number;
  outputPer1M: number;
  name?: string;
}

interface PromptModelGridProps {
  reports: ReportSummary[];
  prompts: PromptSummary[];
  onCellClick: (reportId: string) => void;
  /** Called when user clicks an empty cell to trigger an eval */
  onRunEval?: (promptName: string, modelName: string) => void;
  /** Jobs currently running — shown as progress indicators in cells */
  runningJobs?: GridCellJob[];
  /** Navigate to a job detail page */
  onJobClick?: (jobId: string) => void;
  /** OpenRouter pricing data keyed by model ID */
  modelPricing?: Record<string, ModelPricing>;
  /** Called when user clicks a prompt name to see cost breakdown */
  onPromptCostClick?: (promptName: string) => void;
}

function scoreColor(score: number): {bg: string; color: string} {
  if (score >= 90) return {bg: '#D1FAE5', color: '#065F46'};
  if (score >= 70) return {bg: '#FEF3C7', color: '#92400E'};
  if (score >= 50) return {bg: '#FED7AA', color: '#9A3412'};
  return {bg: '#FEE2E2', color: '#991B1B'};
}

export default function PromptModelGrid({reports, prompts, onCellClick, onRunEval, runningJobs, onJobClick, modelPricing, onPromptCostClick}: PromptModelGridProps) {
  // Build lookup: (promptName, modelName) -> latest report
  const {modelNames, grid} = useMemo(() => {
    const models = new Set<string>();
    const lookup = new Map<string, Map<string, ReportSummary>>();

    for (const r of reports) {
      if (!r.modelName) continue;
      models.add(r.modelName);
      if (!lookup.has(r.promptName)) lookup.set(r.promptName, new Map());
      const byModel = lookup.get(r.promptName)!;
      const existing = byModel.get(r.modelName);
      if (!existing || r.ranAt > existing.ranAt) {
        byModel.set(r.modelName, r);
      }
    }

    const sorted = Array.from(models).sort((a, b) => {
      const countA = reports.filter(r => r.modelName === a).length;
      const countB = reports.filter(r => r.modelName === b).length;
      return countB - countA;
    });

    return {modelNames: sorted, grid: lookup};
  }, [reports]);

  // Build running jobs lookup
  const runningLookup = useMemo(() => {
    const map = new Map<string, GridCellJob>();
    for (const j of runningJobs || []) {
      map.set(`${j.promptName}::${j.modelName}`, j);
    }
    return map;
  }, [runningJobs]);

  if (modelNames.length === 0 || prompts.length === 0) {
    return (
      <Box sx={{textAlign: 'center', py: 6, color: '#9CA3AF'}}>
        <Typography sx={{fontSize: 14}}>No eval results yet. Run an eval to see the grid.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'auto'}}>
      <Table size="small" sx={{tableLayout: 'fixed', minWidth: 200 + modelNames.length * 80}}>
        <TableHead>
          <TableRow sx={{bgcolor: '#F9FAFB'}}>
            <TableCell
              sx={{
                fontSize: 12, fontWeight: 600, color: '#6B7280',
                position: 'sticky', left: 0, bgcolor: '#F9FAFB', zIndex: 2,
                width: 200, minWidth: 200,
                borderRight: '1px solid #E5E7EB',
              }}
            >
              Prompt
            </TableCell>
            {modelNames.map(m => (
              <TableCell
                key={m}
                align="center"
                sx={{width: 80, minWidth: 80, px: 0.5, py: 1, verticalAlign: 'bottom'}}
              >
                <Tooltip title={(() => {
                  const p = modelPricing?.[m];
                  const name = p?.name || m;
                  const price = p ? ` — $${formatPricePer1M(p.inputPer1M)} in / $${formatPricePer1M(p.outputPer1M)} out per 1M tokens` : '';
                  return `${name}${price}`;
                })()}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#6B7280',
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxHeight: 100,
                      mx: 'auto',
                    }}
                  >
                    {m.split('/').pop()}
                  </Typography>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {prompts.map((p) => {
            const byModel = grid.get(p.name);
            return (
              <TableRow key={p.name} hover>
                <TableCell
                  onClick={() => onPromptCostClick?.(p.name)}
                  sx={{
                    fontSize: 13, fontWeight: 500, color: '#111827',
                    position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1,
                    borderRight: '1px solid #E5E7EB',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    cursor: onPromptCostClick ? 'pointer' : 'default',
                    '&:hover': onPromptCostClick ? {bgcolor: '#F9FAFB'} : {},
                  }}
                >
                  <Tooltip title={`${p.name} — click for cost breakdown`}>
                    <span>{p.displayName || p.name}</span>
                  </Tooltip>
                </TableCell>
                {modelNames.map(m => {
                  const report = byModel?.get(m);
                  const runningJob = runningLookup.get(`${p.name}::${m}`);

                  // Running job — show progress
                  if (runningJob) {
                    return (
                      <TableCell key={m} align="center" sx={{px: 1}}>
                        <Tooltip title="Eval running — click to view job">
                          <Box
                            onClick={() => onJobClick?.(runningJob.jobId)}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              width: 32,
                              height: 32,
                            }}
                          >
                            <CircularProgress size={18} thickness={4} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  }

                  // No report — clickable empty cell
                  if (!report) {
                    return (
                      <TableCell key={m} align="center" sx={{px: 1}}>
                        {onRunEval ? (
                          <Tooltip title={`Run eval: ${p.displayName || p.name} × ${m.split('/').pop()}`}>
                            <Box
                              onClick={() => onRunEval(p.name, m)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                cursor: 'pointer',
                                color: '#D1D5DB',
                                transition: 'all 0.15s',
                                '&:hover': {
                                  bgcolor: '#F3F4F6',
                                  color: '#3B82F6',
                                },
                              }}
                            >
                              <AddIcon sx={{fontSize: 16}} />
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography sx={{fontSize: 12, color: '#D1D5DB'}}>---</Typography>
                        )}
                      </TableCell>
                    );
                  }

                  // Has report — show score
                  const score = report.weightedScore ?? 0;
                  const {bg, color} = scoreColor(score);
                  return (
                    <TableCell key={m} align="center" sx={{px: 1}}>
                      <Tooltip title={`${report.passed}/${report.totalCases} passed · ${new Date(report.ranAt).toLocaleDateString()}${report.totalCost != null && report.totalCost > 0 ? ` · ${formatCost(report.totalCost)}` : ''}`}>
                        <Box
                          onClick={() => onCellClick(report.id)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                            bgcolor: bg,
                            color,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: 'pointer',
                            minWidth: 48,
                            transition: 'transform 0.1s, box-shadow 0.1s',
                            '&:hover': {
                              transform: 'scale(1.08)',
                              boxShadow: `0 2px 8px ${bg}`,
                            },
                          }}
                        >
                          {score}%
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
